import React from 'react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { Building2, Users, Shield, GraduationCap } from 'lucide-react';

const FAQPage = () => {
  const enterpriseFAQ = [
    {
      question: 'Comment fonctionne le processus de recrutement chez Nexor Talents ?',
      answer: 'Nous commençons par une écoute approfondie de vos besoins, votre culture et vos objectifs. Ensuite, nous activons notre réseau, sélectionnons les profils les plus pertinents et vous accompagnons jusqu’à l’intégration du talent.',
    },
    {
      question: 'Quels types de postes recrutez-vous ?',
      answer: 'Nous recrutons principalement des profils spécialisés, notamment dans le secteur de l’assurance de dommages : souscripteurs, courtiers/agents, gestionnaires de compte, analystes, experts en sinistre, formateurs, développement des affaires, directeurs… Mais nous restons ouverts à d’autres secteurs selon les besoins.',
    },
    {
      question: 'Combien de temps prend un mandat ?',
      answer: 'Chaque mission est unique, mais nous agissons avec proactivité. Le délai dépend du niveau de spécialisation du poste et de vos critères. Nous vous tenons informés à chaque étape.',
    },
    {
      question: 'Proposez-vous des services de conseil RH ?',
      answer: 'Oui. Nous offrons diverses ressources en RH, notamment des blogs et des rencontres personnalisées.',
    },
  ];

  const candidateFAQ = [
    {
      question: 'Comment puis-je postuler ?',
      answer: 'Vous pouvez nous envoyer votre CV via notre formulaire de contact ou par courriel. Nous vous contacterons si une opportunité correspond à votre profil et vos aspirations.',
    },
    {
      question: 'Est-ce que Nexor Talents m’aide à trouver un emploi ?',
      answer: 'Oui, mais pas n’importe lequel. Nous cherchons à vous connecter à un environnement qui vous ressemble, où votre savoir-faire et vos valeurs seront reconnus.',
    },
    {
      question: 'Dois-je payer pour utiliser vos services ?',
      answer: 'Non. Nos services sont entièrement gratuits pour les candidats.',
    },
    {
      question: 'Que se passe-t-il après l’envoi de mon CV ?',
      answer: 'Nous analysons votre profil avec attention. Si une opportunité se présente, nous vous contactons pour un échange. Sinon, nous gardons votre candidature dans notre réseau pour de futures missions.',
    },
  ];

  const insuranceFAQ = [
    {
      question: 'Pourquoi vous spécialisez-vous dans ce domaine ?',
      answer: 'Travaillant dans l’assurance de dommage depuis de nombreuses années nous comprenons ses enjeux complexes : rigueur, proactivité, expertise technique, changement, adaptabilité. Derrière chaque contrat, il y a des professionnels engagés qui méritent d’être bien accompagnés.',
    },
    {
      question: 'Travaillez-vous avec des compagnies, des cabinets ou des courtiers ?',
      answer: 'Oui, nous collaborons avec tous les acteurs du secteur — assureurs, cabinets d’assurance, grossistes, indépendants, etc. — pour répondre à leurs besoins spécifiques.',
    },
  ];

  return (
    <div className="pt-16">
      {/* Hero Section */}
      <section className="relative min-h-[60vh] flex items-center justify-center bg-[#1a1a1a] overflow-hidden">

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-display font-bold text-white mb-6">
            Foire aux questions
          </h1>
          <p className="text-xl md:text-2xl text-gray-300 max-w-3xl mx-auto">
            Vos questions, nos réponses
          </p>
        </div>
      </section>

      {/* Nexi & Élyor - Réflexion */}
      <section className="py-8 bg-white">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <img
            src="/images/mascots/reflexion.png"
            alt="Nexi et Élyor en pleine réflexion"
            className="w-full max-w-lg mx-auto"
          />
        </div>
      </section>

      {/* FAQ Sections */}
      <section className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Entreprises */}
          <div className="mb-20">
            <div className="flex items-center justify-center space-x-3 mb-12">
              <Building2 className="w-10 h-10 text-gold" />
              <h2 className="text-4xl font-display font-bold text-gray-900">
                Pour les employeurs
              </h2>
            </div>
            <Accordion type="single" collapsible className="space-y-4">
              {enterpriseFAQ.map((item, index) => (
                <AccordionItem key={index} value={`enterprise-${index}`} className="border-2 rounded-xl px-6 border-gray-200 hover:border-gold transition-colors">
                  <AccordionTrigger className="text-left font-semibold text-lg text-gray-900 hover:text-gold py-6">
                    {item.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-600 leading-relaxed text-base pt-2 pb-6">
                    {item.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>

          {/* Candidats */}
          <div className="mb-20">
            <div className="flex items-center justify-center space-x-3 mb-12">
              <Users className="w-10 h-10 text-gold" />
              <h2 className="text-4xl font-display font-bold text-gray-900">
                Pour les candidats
              </h2>
            </div>
            <Accordion type="single" collapsible className="space-y-4">
              {candidateFAQ.map((item, index) => (
                <AccordionItem key={index} value={`candidate-${index}`} className="border-2 rounded-xl px-6 border-gray-200 hover:border-gold transition-colors">
                  <AccordionTrigger className="text-left font-semibold text-lg text-gray-900 hover:text-gold py-6">
                    {item.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-600 leading-relaxed text-base pt-2 pb-6">
                    {item.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>

          {/* Assurance */}
          <div className="mb-20">
            <div className="flex items-center justify-center space-x-3 mb-12">
              <Shield className="w-10 h-10 text-gold" />
              <h2 className="text-4xl font-display font-bold text-gray-900 text-center">
                Pour le secteur de l'assurance
              </h2>
            </div>
            <Accordion type="single" collapsible className="space-y-4">
              {insuranceFAQ.map((item, index) => (
                <AccordionItem key={index} value={`insurance-${index}`} className="border-2 rounded-xl px-6 border-gray-200 hover:border-gold transition-colors">
                  <AccordionTrigger className="text-left font-semibold text-lg text-gray-900 hover:text-gold py-6">
                    {item.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-600 leading-relaxed text-base pt-2 pb-6">
                    {item.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>

          {/* Formations */}
          <div>
            <div className="flex items-center justify-center space-x-3 mb-12">
              <GraduationCap className="w-10 h-10 text-gold" />
              <h2 className="text-4xl font-display font-bold text-gray-900">
                Formations
              </h2>
            </div>
            <Accordion type="single" collapsible className="space-y-4">
              {[
                {
                  question: 'Les formations sont-elles adaptées au domaine de l\u2019assurance ?',
                  answer: 'Absolument. Nos modules, notamment sur la déontologie et la gestion des notes, sont conçus spécifiquement pour répondre aux exigences professionnelles du secteur.'
                },
                {
                  question: 'Proposez-vous des mandats de formation sur mesure pour les entreprises ?',
                  answer: 'Oui. Nous pouvons adapter nos contenus ou créer de nouveaux modules selon les besoins spécifiques de votre équipe. Contactez-nous via le formulaire pour en discuter.'
                },
                {
                  question: 'Quels sont les formats de formation offerts ?',
                  answer: 'Nous offrons des sessions en virtuel (direct), en présentiel et éventuellement du contenu à la demande pour s\u2019adapter à votre rythme.'
                },
                {
                  question: 'Comment être informé des prochaines dates ?',
                  answer: 'Il suffit de vous inscrire à la liste d\u2019attente sur la page Formations. Vous recevrez alors les dates en priorité avec un tarif préférentiel.'
                }
              ].map((item, index) => (
                <AccordionItem key={index} value={`formation-${index}`} className="border-2 rounded-xl px-6 border-gray-200 hover:border-gold transition-colors">
                  <AccordionTrigger className="text-left font-semibold text-lg text-gray-900 hover:text-gold py-6">
                    {item.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-600 leading-relaxed text-base pt-2 pb-6">
                    {item.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FAQPage;